"use client";

import React, { useEffect, useMemo, useState } from "react";
import { cn } from "@/lib/utils";

export interface SeoPreview {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
}

const TRAILING_URL_PUNCTUATION = /[),.;:!?]+$/;

export function cleanPreviewUrl(rawUrl: string) {
  return rawUrl.replace(TRAILING_URL_PUNCTUATION, "");
}

export function extractFirstUrl(text?: string) {
  if (!text) return null;
  const match = text.match(/(https?:\/\/[^\s<]+)/gi)?.[0];
  return match ? cleanPreviewUrl(match) : null;
}

function getMetaContent(doc: Document, selector: string) {
  return doc.querySelector<HTMLMetaElement>(selector)?.content?.trim() || "";
}

function absolutizeUrl(value: string, baseUrl: string) {
  if (!value) return "";
  try {
    return new URL(value, baseUrl).toString();
  } catch {
    return value;
  }
}

export async function fetchSeoPreview(url: string): Promise<SeoPreview> {
  const response = await fetch(url, { credentials: "include" });
  const html = await response.text();
  const doc = new DOMParser().parseFromString(html, "text/html");
  const title =
    getMetaContent(doc, 'meta[property="og:title"]') ||
    getMetaContent(doc, 'meta[name="twitter:title"]') ||
    doc.querySelector("title")?.textContent?.trim() ||
    url;
  const description =
    getMetaContent(doc, 'meta[property="og:description"]') ||
    getMetaContent(doc, 'meta[name="description"]') ||
    getMetaContent(doc, 'meta[name="twitter:description"]');
  const image =
    getMetaContent(doc, 'meta[property="og:image"]') ||
    getMetaContent(doc, 'meta[name="image"]') ||
    getMetaContent(doc, 'meta[name="twitter:image"]');
  const canonicalUrl =
    getMetaContent(doc, 'meta[property="og:url"]') ||
    getMetaContent(doc, 'meta[name="url"]') ||
    url;

  return {
    title,
    description,
    image: absolutizeUrl(image, url),
    url: canonicalUrl,
  };
}

export interface LinkPreviewV2Props {
  url: string;
  initialPreview?: SeoPreview | null;
  variant?: "chat" | "story";
  className?: string;
  onPreviewLoaded?: (preview: SeoPreview | null) => void;
}

export function LinkPreviewV2({
  url,
  initialPreview = null,
  variant = "chat",
  className,
  onPreviewLoaded,
}: LinkPreviewV2Props) {
  const [preview, setPreview] = useState<SeoPreview | null>(initialPreview);
  const [failed, setFailed] = useState(false);

  const hasInitialPreview = useMemo(
    () =>
      Boolean(
        initialPreview?.title ||
          initialPreview?.description ||
          initialPreview?.image
      ),
    [initialPreview]
  );

  useEffect(() => {
    if (hasInitialPreview) {
      setPreview(initialPreview);
      setFailed(false);
    }
  }, [hasInitialPreview, initialPreview]);

  useEffect(() => {
    let cancelled = false;

    const loadPreview = async () => {
      try {
        const nextPreview = await fetchSeoPreview(url);
        if (cancelled) return;
        setPreview(nextPreview);
        setFailed(false);
        onPreviewLoaded?.(nextPreview);
      } catch {
        if (!cancelled) {
          if (hasInitialPreview) {
            onPreviewLoaded?.(initialPreview);
            return;
          }
          setFailed(true);
          onPreviewLoaded?.(null);
        }
      }
    };

    loadPreview();
    return () => {
      cancelled = true;
    };
  }, [url, hasInitialPreview, initialPreview, onPreviewLoaded]);

  if (failed && !preview) return null;

  const isStory = variant === "story";

  if (!preview) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          "block rounded-xl border border-slate-200/70 bg-slate-50/80 p-2 text-[11px] text-indigo-600 underline-offset-2 hover:underline dark:border-zinc-800/80 dark:bg-zinc-900/60 dark:text-indigo-300",
          isStory &&
            "w-full max-w-sm border-white/15 bg-white/10 p-3 text-sm text-white hover:text-white",
          className
        )}
      >
        {url}
      </a>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        "block overflow-hidden rounded-xl border border-slate-200/80 bg-white/80 text-left shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition hover:border-indigo-200 hover:bg-indigo-50/40 dark:border-zinc-800/80 dark:bg-zinc-900/70 dark:hover:border-indigo-500/30 dark:hover:bg-indigo-500/10",
        isStory &&
          "w-full max-w-sm border-white/15 bg-black/45 shadow-[0_12px_40px_rgba(0,0,0,0.35)] backdrop-blur-md hover:border-white/25 hover:bg-black/55",
        className
      )}
    >
      {preview.image ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={preview.image}
          alt=""
          className={cn(
            "w-full object-cover",
            isStory ? "h-40" : "h-24"
          )}
          loading="lazy"
        />
      ) : null}
      <span className={cn("block p-2", isStory && "p-3")}>
        <span
          className={cn(
            "line-clamp-2 block text-[12px] font-semibold leading-snug text-slate-900 dark:text-zinc-100",
            isStory && "text-[15px] text-white"
          )}
        >
          {preview.title || url}
        </span>
        {preview.description ? (
          <span
            className={cn(
              "mt-0.5 line-clamp-3 block text-[11px] leading-snug text-slate-500 dark:text-zinc-400",
              isStory && "mt-1 text-[13px] text-white/75"
            )}
          >
            {preview.description}
          </span>
        ) : null}
        <span
          className={cn(
            "mt-1 block truncate text-[10px] font-medium text-indigo-500 dark:text-indigo-300",
            isStory && "mt-2 text-[11px] text-indigo-200"
          )}
        >
          {preview.url || url}
        </span>
      </span>
    </a>
  );
}
